import hateoas from 'viralstyle-ui/src/hateoas';
import storage from 'viralstyle-ui/src/storage';
import eventHub from 'viralstyle-ui/src/eventHub';
import cloneDeep from 'lodash/cloneDeep';
import each from 'lodash/each';
import get from 'lodash/get';
import find from 'lodash/find';
import isObject from 'lodash/isObject';

import paymentTokens from '../../../utils/payment-tokens';
import router from 'router';
import config from 'config';
import { CustomerModel } from './models';

export const login = async ({ rootState, state, dispatch, commit }, { token, email, password }) => {
  email = email || state.customer.email;
  password = password || state.customer.password;

  try {
    let response = await dispatch('loginLegacy', { email, password, token });

    response = await dispatch('getUserInfo');

    if (response.data.role.includes('CLIENT')) {
      dispatch('eventLogin');
    }

    return response;
  } catch (e) {
    commit('addNotification', 'Incorrect login information, please try again.');

    throw e;
  }
};

export const logout = async ({ rootState, state, dispatch, commit }, isTimeout) => {
  let response;

  if (!rootState.isWhitelabel) {
    commit('clearUserTimeout');
  }

  if (state.customer.is_impersonation) {
    await dispatch('logoutImpersonation', true);
  }

  if (rootState.userLoggedIn) {
    response = await dispatch('logoutLegacy');
  }

  if (!isTimeout) {
    response = await dispatch('getPublicToken');
  } else {
    // only clear the user info if the user explicitly logs out
    commit('updateState', { object: state, property: 'customer', value: new CustomerModel(), });
  }

  commit('updateState', { object: rootState, property: 'userLoggedIn', value: false, });
  commit('updateState', { object: state, property: 'paymentInfo', value: {}, });
  commit('updateState', { object: state, property: 'paymentMethod', value: 'new', });
  commit('updateState', { object: rootState.cart, property: 'showUsOnlyMessage', value: false });

  return response;
};

export const getUserInfo = async ({ rootState, state, dispatch, commit }) => {
  let previousCountry = state.customer.country;

  try {
    let response = await dispatch('get', hateoas.url('get_current_customer_info'));

    if (['signIn', 'signUp'].includes(rootState.route.name) && rootState.account.redirect) {
      if (rootState.account.redirect) {
        if (isObject(rootState.account.redirect)) {
          router.push(rootState.account.redirect);
        } else if (response.data.role === 'CUSTOMER') {
          router.push({ name: 'profile' });
        } else {
          if (rootState.route.query.return) {
            window.location.href = `${config.APP_PROTOCOL}://${config.APP_URL}${rootState.route.query.return}`;
          } else {
            window.location.href = response.data.dashboard_url;
          }
        }
      }
    }

    commit('updateState', { object: state, property: 'customer', value: new CustomerModel(response.data), });
    commit('updateStateClone', { object: state, property: 'cache.customer', value: state.customer, });
    commit('updateState', { object: rootState, property: 'userLoggedIn', value: true, });

    if (Object.keys(response.data.payment_info).length) {
      commit('updateState', { object: state, property: 'paymentInfo', value: response.data.payment_info, });
      commit('updateState', { object: state, property: 'paymentMethod', value: 'saved', });
    }

    if (response.data.role === 'CUSTOMER') {
      dispatch('eventIdentify');
    } else if (['SUPER-ADMIN', 'ADMIN', 'CUSTOMER-SERVICE'].includes(response.data.role)) {
      commit('updateState', { object: rootState, property: 'isUserAdmin', value: true });
    }

    if (rootState.route.name === 'checkout') {
      dispatch('trackAbandonedCart');
    }

    patchCartGeoHelper(rootState, state, dispatch, commit, previousCountry);

    commit('initializeUserTimeout', dispatch);

    eventHub.$emit('userLoggedIn');

    return response;
  } catch (e) {
    if (!rootState.isWhitelabel) {
      commit('clearUserTimeout');
    }

    commit('updateState', { object: rootState, property: 'userLoggedIn', value: false, });

    if (storage.get('saved-address')) {
      commit('updateState', { object: state, property: 'customer', value: new CustomerModel(storage.get('saved-address'), true), });

      dispatch('eventIdentify');

      if (rootState.route.name === 'checkout') {
        dispatch('trackAbandonedCart');
      }
    }

    patchCartGeoHelper(rootState, state, dispatch, commit, previousCountry);

    throw e;
  }
};

let patchCartGeoHelper = async (rootState, state, dispatch, commit, previousCountry) => {
  if (state.customer.country !== 'US' && router.currentRoute.name === 'checkout') {
    each(get(rootState.cart.cart, 'items'), item => {
      if (item.us_only && state.customer.country) {
        commit('updateState', { object: state.customer, property: 'country', value: 'US' });

        commit('updateState', { object: rootState.cart, property: 'showUsOnlyMessage', value: true });

        return false;
      }
    });
  }

  if (!previousCountry || state.customer.country !== previousCountry) {
    dispatch('getCurrencyInfo', state.customer.country);
  }

  if (rootState.cart.cart.items.length) {
    dispatch('patchCartGeo');
  }
};

export const checkout = async ({ rootState, state, dispatch, commit }) => {
  let response,
    payload = {
      customer: cloneDeep(state.customer),
      visit_ids: {},
    };

  if (config.SMARTY_STREETS.ENABLED && state.customer.country === 'US') {
    try {
      let params = [];

      params.push(`auth-id=${config.SMARTY_STREETS.ID}`);
      params.push(`street=${state.customer.street_1}`);
      params.push(`city=${state.customer.city}`);
      params.push(`state=${state.customer.province}`);

      response = await dispatch('externalGet', `https://api.smartystreets.com/street-address?${params.join('&')}`);

      if (!response.data.length) {
        commit('addNotification', 'Invalid Address: Please correct your shipping address so we can deliver your purchase.');
        throw 'Invalid Address';
      }
    } catch (e) {}
  }

  payload.card = await dispatch('getCardPayload');

  delete payload.customer.password;
  delete payload.customer.remember;
  delete payload.customer._links;

  if (state.card.remember) {
    await dispatch('updateCustomerCard', payload.card);
    await dispatch('getUserInfo');
    payload.card.token = state.paymentInfo.id
  }

  response = await dispatch('post', { apiUrl: rootState.cart.cart._links.credit_card_checkout, payload });

  dispatch('deleteCart');

  return response;
};

export const getCardPayload = async ({ rootState, state, dispatch, commit }) => {
  let card = {},
    response,
    tokenResponse,
    token;

  if (state.paymentMethod === 'new') {
    response = await dispatch('get', `${hateoas.url('get_checkout_merchant')}`)

    commit('updateState', { object: rootState.dataCollection, property: 'merchant', value: response.data.merchant });

    switch (response.data.merchant) {
      case 'STRIPE':
        tokenResponse = await dispatch('async', () => paymentTokens.generateStripeToken(response.data, state.card));

        token = tokenResponse;

        break;
      case 'BRAINTREE':
        tokenResponse = await dispatch('async', () => paymentTokens.generateBraintreeToken(response.data, state.card, state.customer));

        token = tokenResponse.creditCards[0].nonce;

        break;
      case 'WORLDPAY':
        tokenResponse = await dispatch('async', () => paymentTokens.generateWorldPayToken(response.data, state.card, state.customer));

        token = tokenResponse;

        break;
    }

    card.cc_bin = state.card.number.replace(/\s/g, '').substr(0, 6);
    card.token = token;
  } else {
    commit('updateState', { object: rootState.dataCollection, property: 'merchant', value: state.paymentInfo.merchant });

    card.token = state.paymentInfo.id;
  }

  return card;
};

export const updateCustomerAddress = async ({ state, dispatch, commit }) => {
  let payload = cloneDeep(state.customer),
    response;

  delete payload.email;
  delete payload.remember;
  delete payload.password;
  delete payload._links;

  response = await dispatch('patch', { apiUrl: state.customer._links.update.href, payload });

  dispatch('getUserInfo');

  return response;
};

export const updateCustomerCard = async ({ state, dispatch, commit }, card) => {
  let payload = card;

  card.last4 = state.card.number.replace(/\s/g, '').slice(-4);

  return dispatch('patch', { apiUrl: state.customer._links.update_payment.href, payload: card });
};

export const trackAbandonedCart = ({ rootState, state, dispatch }) => {
  return dispatch('patch', { apiUrl: rootState.cart.cart._links.track_abandoned_cart, payload: { customer_email: state.customer.email } });
};

export const initializePaypal = ({ rootState, state, dispatch }) => {
  let returnUrl = `https://${window.location.host}/purchase/paypal`,
    callbackUrls = [];

  callbackUrls.push(`callback_urls[success]=${encodeURIComponent(returnUrl)}`);
  callbackUrls.push(`callback_urls[cancel]=${encodeURIComponent(returnUrl)}`);
  callbackUrls.push(`customer_name=${encodeURIComponent(state.customer.name)}`);
  callbackUrls.push(`street_1=${encodeURIComponent(state.customer.street_1)}`);
  callbackUrls.push(`city=${encodeURIComponent(state.customer.city)}`);
  callbackUrls.push(`province=${encodeURIComponent(state.customer.province)}`);
  callbackUrls.push(`zip=${encodeURIComponent(state.customer.postal_code)}`);
  callbackUrls.push(`country=${encodeURIComponent(state.customer.country)}`);
  if (state.customer.street_2) callbackUrls.push(`street_2=${encodeURIComponent(state.customer.street_2)}`);
  if (state.customer.phone) callbackUrls.push(`phone=${encodeURIComponent(state.customer.phone)}`);

  return dispatch('get', `${rootState.cart.cart._links.paypal_initialize}?${callbackUrls.join('&')}`);
};

export const checkoutPaypal = async ({ rootState, dispatch, commit }, data) => {
  let payload = {
    payer_id: data.payerID,
    token: data.paymentToken,
    payment_id: data.paymentID,
    visit_ids: {},
  };

  let response = await dispatch('post', { apiUrl: `${rootState.cart.cart._links.paypal_checkout}`, payload });

  dispatch('deleteCart');

  commit('updateState', { object: rootState.dataCollection, property: 'merchant', value: 'PAYPAL' });
  dispatch('eventCompleteOrder');

  return response
};